import { supabase } from './supabaseClient';

/**
 * Service for managing TikTok settings in Supabase
 */
class TikTokSettingsService {
  /**
   * Load TikTok settings for the current user
   * @returns {Promise<Object|null>} Settings object or null if not found
   */
  async loadSettings() {
    try {
      const session = supabase.getSession();
      const user = supabase.getUser();
      
      const userId = session?.user?.id || session?.user?.sub || user?.id || user?.sub;
      
      if (!userId) {
        console.error('No authenticated user found');
        return null;
      }

      const { data, error } = await supabase
        .from('tiktok_settings')
        .select('*')
        .eq('user_id', userId);

      if (error) {
        console.error('Error loading TikTok settings:', error);
        return null;
      }

      // If no settings found, return null
      if (!data || data.length === 0) {
        console.log('No TikTok settings found for user');
        return null;
      }

      const savedSettings = data[0];
      
      return {
        privacyLevel: savedSettings.privacy_level || '',
        disableComment: savedSettings.disable_comment ?? true,
        disableDuet: savedSettings.disable_duet ?? true,
        disableStitch: savedSettings.disable_stitch ?? true,
        brandContentToggle: savedSettings.brand_content_toggle ?? false,
        yourBrand: savedSettings.your_brand ?? false,
        brandedContent: savedSettings.branded_content ?? false
      };
    } catch (err) {
      console.error('Error loading TikTok settings:', err);
      return null;
    }
  }

  /**
   * Save or update TikTok settings for the current user
   * @param {Object} settings - Settings object
   * @returns {Promise<boolean>} Success status
   */
  async saveSettings(settings) {
    try {
      const session = supabase.getSession();
      const user = supabase.getUser();
      
      const userId = session?.user?.id || session?.user?.sub || user?.id || user?.sub;
      
      if (!userId) {
        console.error('No authenticated user found');
        return false;
      }

      const settingsData = {
        user_id: userId,
        privacy_level: settings.privacyLevel || null,
        disable_comment: settings.disableComment ?? true,
        disable_duet: settings.disableDuet ?? true,
        disable_stitch: settings.disableStitch ?? true,
        brand_content_toggle: settings.brandContentToggle ?? false,
        your_brand: settings.yourBrand ?? false,
        branded_content: settings.brandedContent ?? false
      };

      // Check if settings exist
      const { data: existing } = await supabase
        .from('tiktok_settings')
        .select('id')
        .eq('user_id', userId);

      // If settings exist, delete them first
      if (existing && existing.length > 0) {
        const { error: deleteError } = await supabase
          .from('tiktok_settings')
          .delete()
          .eq('user_id', userId);
          
        if (deleteError) {
          console.error('Error deleting old TikTok settings:', deleteError);
          return false;
        }
      }
      
      // Insert new/updated settings
      const { error } = await supabase
        .from('tiktok_settings')
        .insert(settingsData);

      if (error) {
        console.error('Error saving TikTok settings:', error);
        return false;
      }

      console.log('TikTok settings saved successfully');
      return true;
    } catch (err) {
      console.error('Error saving TikTok settings:', err);
      return false;
    }
  }

  /**
   * Delete TikTok settings for the current user
   * @returns {Promise<boolean>} Success status
   */
  async deleteSettings() {
    try {
      const session = supabase.getSession();
      const user = supabase.getUser();
      
      const userId = session?.user?.id || session?.user?.sub || user?.id || user?.sub;
      
      if (!userId) {
        console.error('No authenticated user found');
        return false;
      }

      const { error } = await supabase
        .from('tiktok_settings')
        .delete()
        .eq('user_id', userId);

      if (error) {
        console.error('Error deleting TikTok settings:', error);
        return false;
      }

      console.log('TikTok settings deleted successfully');
      return true;
    } catch (err) {
      console.error('Error deleting TikTok settings:', err);
      return false;
    }
  }
}

const tiktokSettingsService = new TikTokSettingsService();
export default tiktokSettingsService;
